import { useAuth } from "@clerk/clerk-react";
import {
  Alert,
  Box,
  Grid,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { TargetedCheckInsContext } from "contexts/TargetedCheckIns";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { GmailThread } from "schemas/dashboard";
import { MessageAttachment } from "schemas/email";
import { routes } from "schemas/routes";

import EmailThread from "components/EmailThread";
import EmailDraftComponentAction from "components/EmailThread/EmailDraft/action";
import { fetcherAuth } from "utils/api";
import { openBase64DataInTab } from "utils/files";

import { noThreadFound } from "../../../helpers";
import styles from "./styles";

export default function ThreadPreview() {
  const { actionItemId, outreachContactId, threadId } = useParams();
  const { actionItem, setActionItem, fetchSingleActionItem } = useContext(
    TargetedCheckInsContext,
  );
  const { getToken } = useAuth();

  const { currentOrg, currentUser } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { fetchIntegrationHealth, userIntegration } = useContext(
    UserIntegrationsContext,
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [gmailThread, setGmailThread] = useState<GmailThread | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const [missingIntegration, setMissingIntegration] = useState(false);

  const fetchEmailThread = async () => {
    if (!currentOrg) {
      return;
    }
    setGmailThread(undefined);
    setLoading(true);
    try {
      const { gmailThread } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/${actionItemId}/threads/${threadId}`,
        "GET",
      );
      setGmailThread(gmailThread);
    } catch (error) {
      if (error?.message?.includes("Google integration")) {
        setMissingIntegration(true);
      } else {
        setErrorAlert(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id && outreachContactId && threadId) {
      fetchEmailThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, outreachContactId, threadId]);

  useEffect(() => {
    if (currentOrg?.id && actionItemId && !actionItem?.id) {
      fetchSingleActionItem(Number(actionItemId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, actionItemId]);

  const openAttachment = async (
    messageId: string,
    attachment: MessageAttachment,
  ) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-integrations/${userIntegration?.id}/messages/${messageId}/attachments/${attachment.attachment_id}`,
        "GET",
      );
      openBase64DataInTab(res.attachment.data, attachment.mime_type);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  return (
    <Box>
      {loading ? (
        <Grid
          container
          sx={styles.progressContainer}
          gap={2}
          alignItems="center"
          justifyContent="center"
        >
          <Skeleton
            animation="wave"
            variant="rounded"
            height={40}
            width="90%"
          />
          <Skeleton
            animation="wave"
            variant="rounded"
            height={30}
            width="90%"
          />
          <Skeleton
            animation="wave"
            variant="rounded"
            height={400}
            width="90%"
          />
        </Grid>
      ) : (
        <>
          {missingIntegration && (
            <Alert severity="error">
              Log in to Google on your{" "}
              <Link style={{ color: "black" }} to={`/${routes.settings}`}>
                settings page
              </Link>{" "}
              to continue using this feature
            </Alert>
          )}

          {gmailThread && currentUser && gmailThread?.messages?.length > 0 ? (
            <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <EmailThread
                messages={gmailThread.messages}
                openAttachment={openAttachment}
                showSubject={!isSmallScreen}
              />
            </Box>
          ) : (
            <>{!loading && noThreadFound}</>
          )}
          {actionItem && (
            <EmailDraftComponentAction
              actionItem={actionItem}
              setActionItem={setActionItem}
              gmailThread={gmailThread}
              loading={loading}
            />
          )}
        </>
      )}
    </Box>
  );
}
